import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { runMatch, type MatchResult, type Lineup } from '@stanwars/sim';
import { BattleScene } from '../battle/BattleScene';
import { content } from '../content/bundle';
import { PixelButton } from '../ui/PixelButton';
import { PixelText } from '../ui/PixelText';

function buildLineupFromContent(memberIds: string[], playerId: string): Lineup {
  const slots = memberIds.map((memberId, i) => {
    const member = content.getMember(memberId);
    if (!member) throw new Error(`Unknown member: ${memberId}`);
    const skills = content.getSkillsForMember(memberId);
    const passive = skills.find(s => s.type === 'passive');
    const actives = skills.filter(s => s.type === 'active');
    const ult = skills.find(s => s.type === 'ultimate');
    if (!passive || actives.length < 2 || !ult)
      throw new Error(`Incomplete skills for ${memberId}`);
    return {
      member: {
        id: member.id,
        stageName: member.stageName.parody,
        groupId: member.groupId,
        role: member.role,
        rarity: member.rarity,
        stats: member.stats
      },
      position: i as 0 | 1 | 2 | 3 | 4,
      loadout: {
        passiveId: passive.id,
        activeIds: [actives[0]!.id, actives[1]!.id] as [string, string],
        ultimateId: ult.id
      },
      skills: [passive, actives[0]!, actives[1]!, ult].map(s => ({
        id: s.id,
        ownerMemberId: s.ownerMemberId,
        type: s.type,
        archetypeId: s.archetypeId,
        params: s.params,
        cooldown: s.cooldown,
        flavorName: s.flavorName,
        flavorText: s.flavorText
      }))
    };
  });
  return { playerId, slots: slots as Lineup['slots'] };
}

const LINEUP_A = ['jungkook', 'lisa', 'karina', 'felix', 'hanni'];
const LINEUP_B = ['rm', 'jennie', 'winter', 'bangchan', 'minji'];

export function Exhibition() {
  const { t } = useTranslation();
  const [result, setResult] = useState<MatchResult | null>(null);
  const [lineups, setLineups] = useState<{ A: Lineup; B: Lineup } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const A = buildLineupFromContent(LINEUP_A, 'exhibitionA');
      const B = buildLineupFromContent(LINEUP_B, 'exhibitionB');
      setLineups({ A, B });
      setResult(runMatch(20260511, A, B));
    } catch (e) {
      setError(String(e));
    }
  }, []);

  if (error) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4 p-4">
        <PixelText color="yellow">{t('exhibition.error', { error })}</PixelText>
        <Link to="/"><PixelButton variant="ghost">{t('exhibition.home')}</PixelButton></Link>
      </main>
    );
  }

  if (!result || !lineups) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center p-4">
        <PixelText>{t('exhibition.loading')}</PixelText>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center p-4">
      <BattleScene result={result} lineupA={lineups.A} lineupB={lineups.B} />
      <Link to="/" className="mt-6"><PixelButton variant="ghost">{t('exhibition.home')}</PixelButton></Link>
    </main>
  );
}
